import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ShieldCheckIcon,
  BoltIcon,
  EyeIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import Card from '../common/Card';
import Button from '../common/Button';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { remediationAPI } from '../../services/api';

const SEVERITY_BADGE = {
  critical: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  low: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
};

const PROVIDER_LABEL = { microsoft: 'Microsoft 365', google: 'Google Workspace' };
const TERMINAL = ['completed', 'partial', 'failed', 'rolled_back'];

function fmtSeconds(s) {
  if (!s) return '—';
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  return `${m} min`;
}

/**
 * One-click security hardening panel.
 *
 * Loads the current plan + posture score, lets an admin Preview (dry run) or
 * "Fix It All" (apply). The apply flow shows a confirmation modal first, then
 * polls the queued job to completion and renders the result + score delta.
 */
export default function FixItAllPanel() {
  const toast = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';

  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);       // { summary, preflight, actions, currentScore }
  const [showConfirm, setShowConfirm] = useState(false);
  const [running, setRunning] = useState(false);
  const [job, setJob] = useState(null);         // live job during/after apply
  const pollRef = useRef(null);

  const loadPlan = useCallback(async () => {
    setLoading(true);
    try {
      const res = await remediationAPI.getPlan('high');
      setPlan(res.data.data);
    } catch (err) {
      toast.error('Failed to load remediation plan');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadPlan();
    return () => clearInterval(pollRef.current);
  }, [loadPlan]);

  const pollJob = useCallback((jobId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await remediationAPI.getJob(jobId);
        const j = res.data.data;
        setJob(j);
        if (TERMINAL.includes(j.status)) {
          clearInterval(pollRef.current);
          setRunning(false);
          const delta = (j.scoreAfter ?? 0) - (j.scoreBefore ?? 0);
          toast.success(`Remediation ${j.status}. Score ${j.scoreBefore} → ${j.scoreAfter}${delta > 0 ? ` (+${delta})` : ''}`);
          loadPlan();
        }
      } catch (_) {
        clearInterval(pollRef.current);
        setRunning(false);
      }
    }, 2000);
  }, [toast, loadPlan]);

  const handlePreview = async () => {
    try {
      const res = await remediationAPI.preview('high');
      const previewJob = res.data.data.job;
      const full = await remediationAPI.getJob(previewJob.id);
      setJob(full.data.data);
      toast.info(`Preview ready: ${res.data.data.actionCount} action(s) would run. No changes were made.`);
    } catch (err) {
      toast.error('Preview failed');
    }
  };

  const handleRun = async () => {
    setShowConfirm(false);
    setRunning(true);
    try {
      const res = await remediationAPI.run('high');
      const queued = res.data.data.job;
      if (!queued) {
        toast.info(res.data.data.message || 'Nothing to remediate.');
        setRunning(false);
        return;
      }
      setJob(queued);
      pollJob(queued.id);
    } catch (err) {
      setRunning(false);
      if (err.response?.status === 412) {
        toast.error('Required permissions not granted yet — a Global Admin must grant admin consent.');
      } else {
        toast.error('Failed to start remediation');
      }
    }
  };

  if (loading) {
    return (
      <Card>
        <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400">
          <ArrowPathIcon className="h-5 w-5 animate-spin" />
          Loading remediation engine…
        </div>
      </Card>
    );
  }

  const summary = plan?.summary || { total: 0, bySeverity: {}, byProvider: {} };
  const preflightOk = plan?.preflight?.ok !== false;
  const score = plan?.currentScore ?? 0;
  const scoreColor = score >= 80 ? 'text-green-600' : score >= 50 ? 'text-yellow-600' : 'text-red-600';

  return (
    <Card className="relative overflow-hidden">
      <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
        {/* Score + counts */}
        <div className="flex items-center gap-5">
          <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-700">
            <ShieldCheckIcon className={`h-9 w-9 ${scoreColor}`} />
          </div>
          <div>
            <div className="flex items-baseline gap-2">
              <span className={`text-3xl font-bold ${scoreColor}`}>{score}</span>
              <span className="text-slate-400">/100 posture</span>
            </div>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {summary.total > 0
                ? `${summary.total} fix${summary.total > 1 ? 'es' : ''} available`
                : 'No high-severity fixes pending'}
              {summary.bySeverity?.critical ? ` · ${summary.bySeverity.critical} critical` : ''}
              {summary.bySeverity?.high ? ` · ${summary.bySeverity.high} high` : ''}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <Button variant="outline" size="md" onClick={handlePreview} disabled={summary.total === 0}>
            <EyeIcon className="mr-2 h-4 w-4" /> Preview
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => setShowConfirm(true)}
            loading={running}
            disabled={summary.total === 0 || !isAdmin}
            title={!isAdmin ? 'Only admins can run remediation' : undefined}
          >
            <BoltIcon className="mr-2 h-4 w-4" /> Fix It All
          </Button>
        </div>
      </div>

      {!isAdmin && summary.total > 0 && (
        <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-400">
          <LockClosedIcon className="h-4 w-4" /> Only an admin or owner can apply fixes.
        </p>
      )}

      {!preflightOk && (
        <div className="mt-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-amber-600" />
          <p className="text-sm text-amber-700 dark:text-amber-300">
            Some fixes need permissions that haven’t been granted yet. A Global Admin must grant admin consent
            for the requested write scopes before these can be applied.
          </p>
        </div>
      )}

      {job && (
        <JobResult
          job={job}
          isAdmin={isAdmin}
          onChanged={async () => {
            try {
              const res = await remediationAPI.getJob(job.id);
              setJob(res.data.data);
              loadPlan();
            } catch (_) { /* noop */ }
          }}
        />
      )}

      {showConfirm && (
        <ConfirmModal
          plan={plan}
          onConfirm={handleRun}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </Card>
  );
}

/** Confirmation modal — shows exactly what will happen before applying. */
function ConfirmModal({ plan, onConfirm, onCancel }) {
  const s = plan.summary;
  const platforms = Object.keys(s.byProvider || {}).map((p) => PROVIDER_LABEL[p] || p).join(', ');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl dark:bg-slate-800">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Apply {s.total} security fix{s.total > 1 ? 'es' : ''}?</h3>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Here’s exactly what will happen:</p>

        <dl className="mt-4 space-y-2 text-sm">
          <Row label="Issues fixed">
            {s.bySeverity?.critical ? <Badge sev="critical">{s.bySeverity.critical} critical</Badge> : null}
            {s.bySeverity?.high ? <Badge sev="high">{s.bySeverity.high} high</Badge> : null}
            {s.bySeverity?.medium ? <Badge sev="medium">{s.bySeverity.medium} medium</Badge> : null}
          </Row>
          <Row label="Platforms">{platforms || '—'}</Row>
          <Row label="Permissions">{(s.requiredScopes || []).length} scope(s) required</Row>
          <Row label="Estimated time">{fmtSeconds(s.estimateSeconds)}</Row>
          <Row label="Reversible">{s.reversible ? 'Yes — you can undo this' : 'Some actions cannot be undone'}</Row>
          <Row label="Compliance">{(s.complianceTags || []).join(', ') || '—'}</Row>
        </dl>

        <div className="mt-4 rounded-lg bg-slate-50 p-3 text-xs text-slate-500 dark:bg-slate-700/50 dark:text-slate-400">
          Every change is recorded in your audit log, and you can undo this run afterwards.
        </div>

        <div className="mt-5 flex justify-end gap-3">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button variant="primary" onClick={onConfirm}>
            <BoltIcon className="mr-2 h-4 w-4" /> Confirm &amp; Fix
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Live/finished job result with per-action status + undo. */
function JobResult({ job, isAdmin, onChanged }) {
  const toast = useToast();
  const actions = job.actions || [];
  const isPreview = job.mode === 'preview';
  const done = TERMINAL.includes(job.status);
  const [busy, setBusy] = useState(false);

  const canUndoRun = !isPreview && isAdmin && actions.some((a) => a.status === 'succeeded' && a.reversible);

  const undoRun = async () => {
    setBusy(true);
    try {
      const res = await remediationAPI.rollbackJob(job.id);
      toast.success(`Undid ${res.data.data.undone} change(s).`);
      await onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Undo failed');
    } finally {
      setBusy(false);
    }
  };

  const undoAction = async (actionId) => {
    setBusy(true);
    try {
      await remediationAPI.rollbackAction(actionId);
      toast.success('Change reverted.');
      await onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Undo failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-5 border-t border-slate-200 pt-4 dark:border-slate-700">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="font-medium text-slate-900 dark:text-white">
          {isPreview ? 'Preview — no changes made' : done ? `Run ${job.status}` : 'Running…'}
        </h4>
        <div className="flex items-center gap-3">
          {!isPreview && job.scoreAfter != null && (
            <span className="text-sm text-slate-500">
              Score {job.scoreBefore} → <span className="font-semibold text-green-600">{job.scoreAfter}</span>
            </span>
          )}
          {canUndoRun && (
            <Button variant="outline" size="sm" onClick={undoRun} loading={busy}>
              <ArrowPathIcon className="mr-1.5 h-4 w-4" /> Undo this run
            </Button>
          )}
        </div>
      </div>
      <ul className="space-y-2">
        {actions.map((a) => (
          <li key={a.id} className="flex items-start gap-3 text-sm">
            <StatusIcon status={a.status} />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-slate-700 dark:text-slate-200">{a.title}</span>
                <Badge sev={a.severity}>{a.severity}</Badge>
                {a.status === 'succeeded' && a.reversible && isAdmin && (
                  <button onClick={() => undoAction(a.id)} disabled={busy} className="text-xs text-primary-600 hover:underline disabled:opacity-50">
                    Undo
                  </button>
                )}
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">{a.plan}</p>
              {a.errorMessage && <p className="text-xs text-red-500">{a.errorMessage}</p>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatusIcon({ status }) {
  if (status === 'succeeded') return <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-green-500" />;
  if (status === 'failed') return <XCircleIcon className="h-5 w-5 flex-shrink-0 text-red-500" />;
  if (status === 'rolled_back') return <ArrowPathIcon className="h-5 w-5 flex-shrink-0 text-slate-400" />;
  if (status === 'running') return <ArrowPathIcon className="h-5 w-5 flex-shrink-0 animate-spin text-primary-500" />;
  if (status === 'preview') return <EyeIcon className="h-5 w-5 flex-shrink-0 text-slate-400" />;
  return <div className="h-5 w-5 flex-shrink-0 rounded-full border-2 border-slate-300" />;
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="flex flex-wrap items-center justify-end gap-1.5 text-slate-800 dark:text-slate-100">{children}</dd>
    </div>
  );
}

function Badge({ sev, children }) {
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${SEVERITY_BADGE[sev] || SEVERITY_BADGE.low}`}>{children}</span>;
}
